import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, Send } from "lucide-react";
import { CLINIC, SERVICES } from "@/lib/data";
import { useLang } from "@/lib/i18n";

export default function Footer() {
  const { t } = useLang();
  return (
    <footer className="bg-deep text-white/85 pt-20 pb-32 lg:pb-12 relative overflow-hidden" data-testid="site-footer">
      <div className="absolute inset-0 subtle-grain opacity-30 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-4 space-y-5">
            <div className="flex items-center gap-4">
              <img src={CLINIC.logo} alt="SAT logo" className="h-16 bg-white/95 rounded-2xl p-2" />
              <div>
                <div className="font-display text-2xl font-semibold leading-none">Shri Anandpur Trust</div>
                <div className="text-[10px] tracking-[0.22em] uppercase text-gold mt-2 font-semibold">Charitable Diagnostic Centre</div>
              </div>
            </div>
            <p className="text-sm leading-relaxed text-white/70 max-w-sm">{CLINIC.promise}</p>
            <div className="flex items-center gap-2 text-xs text-gold font-semibold tracking-[0.15em] uppercase">
              <span className="w-8 h-px bg-gold" /> {t("footer.tagline")}
            </div>
          </div>

          <div className="lg:col-span-2">
            <h4 className="text-sm font-semibold uppercase tracking-[0.15em] text-white mb-5">{t("footer.visit")}</h4>
            <ul className="space-y-3 text-sm">
              <li><Link to="/about" className="hover:text-gold transition-colors">{t("footer.about")}</Link></li>
              <li><Link to="/doctors" className="hover:text-gold transition-colors">{t("footer.doctors")}</Link></li>
              <li><Link to="/opd" className="hover:text-gold transition-colors">{t("footer.opd")}</Link></li>
              <li><Link to="/gallery" className="hover:text-gold transition-colors">{t("footer.gallery")}</Link></li>
              <li><Link to="/contact" className="hover:text-gold transition-colors">{t("footer.contact")}</Link></li>
              <li><Link to="/book" className="hover:text-gold transition-colors">{t("footer.book")}</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h4 className="text-sm font-semibold uppercase tracking-[0.15em] text-white mb-5">{t("footer.servicesTitle")}</h4>
            <ul className="space-y-3 text-sm">
              {SERVICES.slice(0, 7).map((s) => (
                <li key={s.slug}>
                  <Link to={`/services/${s.slug}`} className="hover:text-gold transition-colors">{s.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h4 className="text-sm font-semibold uppercase tracking-[0.15em] text-white mb-5">{t("footer.reach")}</h4>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-gold shrink-0 mt-0.5" strokeWidth={1.5} />
                <span>{CLINIC.address}</span>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-gold shrink-0 mt-0.5" strokeWidth={1.5} />
                <div>
                  <a href={`tel:${CLINIC.phone}`} className="block hover:text-gold">{CLINIC.phone}</a>
                  <a href={`tel:${CLINIC.altPhone}`} className="block text-white/60 text-xs hover:text-gold">{CLINIC.altPhone}</a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-gold shrink-0 mt-0.5" strokeWidth={1.5} />
                <a href={`mailto:${CLINIC.email}`} className="hover:text-gold">{CLINIC.email}</a>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-gold shrink-0 mt-0.5" strokeWidth={1.5} />
                <span>{t("common.timings.value")}</span>
              </li>
            </ul>

            <form
              className="mt-6 flex items-center bg-white/5 border border-white/10 rounded-full overflow-hidden"
              onSubmit={(e) => { e.preventDefault(); alert("Subscribed!"); }}
              data-testid="newsletter-form"
            >
              <input
                type="email"
                required
                placeholder={t("footer.newsletter")}
                className="bg-transparent flex-1 px-5 py-3 text-sm text-white placeholder-white/40 outline-none"
                data-testid="newsletter-email"
              />
              <button type="submit" className="bg-gold text-deep p-3 m-1 rounded-full hover:bg-gold-600 transition-colors" aria-label="Subscribe" data-testid="newsletter-submit">
                <Send className="w-4 h-4" strokeWidth={2} />
              </button>
            </form>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between gap-4 text-xs text-white/50">
          <div>© {new Date().getFullYear()} Shri Anandpur Trust — {t("footer.rights")}</div>
        </div>
      </div>
    </footer>
  );
}
